import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { useFormat } from "@/lib/format";
import { Card, EmptyState, ErrorState, PageHeader, Spinner, Table } from "@/components/ui";

/**
 * The audit log.
 *
 * Written inside the same transaction as the change it records, so a change cannot exist without
 * it. This is the screen that settles "who moved that balance", months later.
 */
export function AuditPage() {
  const fmt = useFormat();
  const list = useListParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["audit", list.q, list.page],
    queryFn: () =>
      api.get<{
        entries: Array<{
          id: string;
          action: string;
          entity: string;
          entityId: string | null;
          summary: string | null;
          createdAt: string;
          actorName: string | null;
          actorLabel: string | null;
        }>;
        page: PageMeta;
      }>("/settings/audit", { q: list.q, page: list.page }),
    placeholderData: (previous) => previous,
  });

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Audit log"
        back={{ to: "/app/settings", label: "Settings" }}
        subtitle="Who changed what, and when."
        description={
          "Every change, written in the same transaction as the change itself — so nothing can happen " +
          "without a record. This is the screen that settles an argument months later."
        }
      />

      <Card padded={false}>
        <div className="border-b border-ink-200 p-3">
          <SearchBox params={list} placeholder="Search action, summary or who did it…" />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : (data?.entries.length ?? 0) === 0 ? (
          <EmptyState title="Nothing recorded yet." />
        ) : (
          <Table
            head={
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Detail</th>
              </tr>
            }
          >
            {data!.entries.map((entry) => (
              <tr key={entry.id}>
                <td className="whitespace-nowrap text-ink-500">{fmt.dateTime(entry.createdAt)}</td>
                <td>{entry.actorName ?? entry.actorLabel ?? "System"}</td>
                <td className="font-mono text-xs text-ink-600">{entry.action}</td>
                <td className="text-ink-500">{entry.summary ?? entry.entity}</td>
              </tr>
            ))}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun={"entries"} />
      </Card>
    </div>
  );
}
